import React, { useState, useMemo } from "react";
import { useNavigate } from "@tanstack/react-router";
import { format } from "date-fns";
import {
  Layers,
  Hammer,
  CheckCircle2,
  MapPin,
  Loader2,
  Download,
  Clock,
  PackageCheck,
  AlertCircle,
  Image as ImageIcon,
  Tag,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { trpcClient } from "@/lib/trpc";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { getFullUrl } from "@/lib/uploadFile";
import { ProductionThumbnailCell } from "@/components/shared/ProductionThumbnailCell";
import { cn, formatLocation } from "@/lib/utils";
import ImageLightbox from "@/components/shared/ImageLightbox";
import {
  InlineFilterRow,
  CHECKBOX_COL_NULL,
  type ColumnFilter,
} from "@/components/shared/InlineFilterRow";
import type { FilterField, ActiveFilters } from "@/components/shared/FilterBar";
import OutsourceToolingModal from "./OutsourceToolingModal";
import QuickRelocateModal from "./QuickRelocateModal";
import PrintDialog from "@/components/shared/PrintDialog";
import MoldLabelPrintTemplate from "./print/MoldLabelPrintTemplate";
import { MoldWearBar } from "./MoldWearBar";
import {
  DataTableRow,
  DataTableRowCheckbox,
  DataTableSelectAllCheckbox,
} from "@/components/shared/DataTableRow";
import { BulkActionBar, type BulkAction } from "@/components/shared/BulkActionBar";
import { useRowSelection } from "@/hooks/useRowSelection";
import { exportToExcel, type ExcelColumnDef } from "@/lib/excel.utils";
import {
  getMoldTypeVisualMeta,
  normalizeMoldTypeCode,
  type MoldTypeTaxonomyItem,
} from "./mold-taxonomy";

export const MOLD_PLANNING_EXPORT_COLUMNS: ExcelColumnDef[] = [
  { key: "orderId", label: "Lệnh Sản Xuất", width: 18 },
  { key: "productName", label: "Sản Phẩm", width: 32 },
  { key: "customerName", label: "Khách Hàng", width: 28 },
  { key: "moldCode", label: "Mã Khuôn", width: 20 },
  { key: "toolingType", label: "Phân Loại Khuôn", width: 22 },
  { key: "processName", label: "Công Đoạn", width: 20 },
  { key: "location", label: "Vị Trí Kệ", width: 20 },
  { key: "readiness", label: "Trạng Thái", width: 18 },
];

export const MOLD_PLANNING_FILTER_COLUMNS: (ColumnFilter | null)[] = [
  CHECKBOX_COL_NULL,
  { key: "orderCode" },
  { key: "moldCode" },
  { key: "processName" },
  { key: "location" },
  null,
  null,
  null,
];

export const MOLD_PLANNING_FILTER_FIELDS: FilterField[] = [
  { key: "orderCode", label: "Lọc mã LSX / SP...", type: "text" },
  { key: "moldCode", label: "Lọc mã khuôn...", type: "text" },
  { key: "processName", label: "Lọc công đoạn...", type: "text" },
  { key: "location", label: "Lọc vị trí kệ...", type: "text" },
];

export function toMoldPlanningExportRow(
  t: any,
  taxonomyByCode?: Map<string, MoldTypeTaxonomyItem>,
) {
  const canonical = normalizeMoldTypeCode(t.toolingType);
  const meta =
    taxonomyByCode?.get(canonical) || getMoldTypeVisualMeta(t.toolingType);
  const isReady = t.readinessStatus === "READY";
  const isOutsourced = Boolean(t.outsourcePoId) && !isReady;

  return {
    orderId: t.productionOrder?.orderId || "",
    productName: t.productionOrder?.productName || "",
    customerName:
      t.productionOrder?.customer?.fullName ||
      t.productionOrder?.customer?.name ||
      "",
    moldCode: t.moldCode || t.dieCut?.dieCutId || "",
    toolingType: `${meta.prefix} • ${meta.shortLabel}`,
    processName: t.processName || "",
    location: formatLocation(t.location || t.dieCut?.location) || "",
    readiness: isReady
      ? "Sẵn sàng tại kệ"
      : isOutsourced || t.readinessStatus === "IN_PRODUCTION"
        ? "Đang làm ngoài"
        : "Chờ chuẩn bị",
  };
}

export interface MoldPlanningBoardProps {
  items: any[];
  isLoading: boolean;
  onRefetch: () => void;
  showFilter: boolean;
  filterValues: ActiveFilters;
  onFilterChange: (filters: ActiveFilters) => void;
  taxonomyByCode: Map<string, MoldTypeTaxonomyItem>;
}

export default function ToolingTrackingBoard({
  items,
  isLoading,
  onRefetch,
  showFilter,
  filterValues,
  onFilterChange,
  taxonomyByCode,
}: MoldPlanningBoardProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Modal Receive state
  const [isReceiveOpen, setIsReceiveOpen] = useState(false);
  const [activeItem, setActiveItem] = useState<any>(null);
  const [receiveLocation, setReceiveLocation] = useState("KHO-KHUON/K01-B02");
  const [receiveStorageZone, setReceiveStorageZone] = useState("");
  const [receiveStorageRack, setReceiveStorageRack] = useState("");
  const [receiveStorageShelf, setReceiveStorageShelf] = useState("");
  const [receiveStorageBin, setReceiveStorageBin] = useState("");
  const [receiveNotes, setReceiveNotes] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Modal Outsource Tooling state (Rule 8)
  const [isOutsourceOpen, setIsOutsourceOpen] = useState(false);
  const [outsourceItem, setOutsourceItem] = useState<any>(null);

  // Modal Relocate Tooling state
  const [isRelocateOpen, setIsRelocateOpen] = useState(false);
  const [relocateItem, setRelocateItem] = useState<any>(null);

  // Modal Print Label state
  const [isPrintLabelOpen, setIsPrintLabelOpen] = useState(false);
  const [printLabelItem, setPrintLabelItem] = useState<any>(null);

  const invalidateAllMoldQueries = () => {
    queryClient.invalidateQueries({ queryKey: ["tooling-tracking-board"] });
    queryClient.invalidateQueries({ queryKey: ["molds"] });
    queryClient.invalidateQueries({ queryKey: ["molds-stats"] });
    queryClient.invalidateQueries({ queryKey: ["production-order"] });
    onRefetch();
  };

  const handleOpenOutsource = (tool: any) => {
    setOutsourceItem({
      id: tool.id,
      moldCode: tool.moldCode,
      name: tool.dieCut?.name,
      productName: tool.productionOrder?.productName,
      processName: tool.processName,
      toolingType: tool.toolingType,
      dimensions: tool.dimensions || tool.dieCut?.size,
      productionOrderId: tool.productionOrder?.id,
      poCode: tool.productionOrder?.orderId,
      customerName:
        tool.productionOrder?.customer?.fullName ||
        tool.productionOrder?.customer?.name,
    });
    setIsOutsourceOpen(true);
  };

  // ── Chọn nhiều dòng + Xuất Excel ──
  const rowIds = useMemo(() => items.map((t: any) => t.id), [items]);
  const rowSelection = useRowSelection(rowIds);
  const { selectedCount, selectedIds, clearSelection } = rowSelection;

  const bulkActions: BulkAction[] = selectedCount
    ? [
        {
          icon: <Download className="h-4 w-4" />,
          label: "Xuất danh sách chọn",
          variant: "outline",
          onClick: () => {
            exportToExcel(
              items
                .filter((t: any) => selectedIds.has(t.id))
                .map((t: any) => toMoldPlanningExportRow(t, taxonomyByCode)),
              MOLD_PLANNING_EXPORT_COLUMNS,
              "ke_hoach_khuon_da_chon",
            );
            clearSelection();
          },
        },
      ]
    : [];

  // Bấm dòng: mở hồ sơ khuôn; dòng chưa gắn khuôn → mở tab Khuôn mẫu của LSX
  const openRow = (tool: any) => {
    if (tool.dieCut?.id) {
      navigate({ to: "/molds/$moldId", params: { moldId: tool.dieCut.id } });
      return;
    }
    const orderId = tool.productionOrder?.orderId || tool.productionOrderId;
    if (orderId) {
      navigate({
        to: "/production-orders/$orderId",
        params: { orderId },
        search: { tab: "toolings" } as any,
      });
    }
  };

  const handleOpenReceive = (item: any) => {
    setActiveItem(item);
    setReceiveStorageZone(item.dieCut?.storageZone || "");
    setReceiveStorageRack(item.dieCut?.storageRack || "");
    setReceiveStorageShelf(item.dieCut?.storageShelf || "");
    setReceiveStorageBin(item.dieCut?.storageBin || "");
    setReceiveLocation(item.location || item.dieCut?.location || "");
    setReceiveNotes("Nghiệm thu đạt chuẩn kỹ thuật, sẵn sàng chạy máy");
    setIsReceiveOpen(true);
  };

  const handleSubmitReceive = async () => {
    if (!activeItem) return;
    const computedLoc =
      receiveLocation.trim() ||
      [
        receiveStorageZone.trim(),
        receiveStorageRack.trim() ? `Kệ ${receiveStorageRack.trim()}` : "",
        receiveStorageShelf.trim() ? `Tầng ${receiveStorageShelf.trim()}` : "",
        receiveStorageBin.trim() ? `Ô ${receiveStorageBin.trim()}` : "",
      ]
        .filter(Boolean)
        .join(" • ");

    if (!computedLoc) {
      toast.error("Vui lòng nhập vị trí hoặc tọa độ kệ kho lưu khuôn");
      return;
    }
    try {
      setIsSubmitting(true);
      await trpcClient.productionOrders.receiveTooling.mutate({
        id: activeItem.productionOrder?.id || activeItem.productionOrderId,
        toolingId: activeItem.id,
        location: computedLoc,
        storageZone: receiveStorageZone.trim() || undefined,
        storageRack: receiveStorageRack.trim() || undefined,
        storageShelf: receiveStorageShelf.trim() || undefined,
        storageBin: receiveStorageBin.trim() || undefined,
        notes: receiveNotes.trim() || undefined,
      });
      toast.success(
        `Đã nghiệm thu khuôn vào vị trí ${computedLoc} và chuyển trạng thái Sẵn sàng`,
      );
      setIsReceiveOpen(false);
      invalidateAllMoldQueries();
    } catch (err: any) {
      toast.error(err.message || "Lỗi khi nghiệm thu nhập kho");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <table
        aria-label="Kế hoạch khuôn"
        className={cn(
          "w-full text-xs caption-bottom apv-compact-table min-w-[920px] md:table-fixed",
          rowSelection.selectionMode && "apv-select-mode",
        )}
      >
        <thead className="apv-table-header">
          <tr className="whitespace-nowrap">
            <th
              scope="col"
              className="w-9 text-center text-xs font-bold text-slate-700 md:sticky md:left-0 z-30 bg-slate-50 !bg-slate-50 [isolation:isolate] border-r border-slate-100"
            >
              <DataTableSelectAllCheckbox rowSelection={rowSelection} asChild />
            </th>
            <th
              scope="col"
              className="w-[190px] text-left text-xs font-bold text-slate-700 md:sticky md:left-9 z-30 bg-slate-50 !bg-slate-50 [isolation:isolate] border-r border-slate-200 md:shadow-[2px_0_4px_-1px_rgba(0,0,0,0.06)]"
            >
              Lệnh Sản Xuất
            </th>
            <th scope="col" className="w-[154px] text-left text-xs font-bold text-slate-700">
              Mã Khuôn Độc Bản
            </th>
            <th scope="col" className="w-[116px] text-left text-xs font-bold text-slate-700">
              Phân Loại &amp; CĐ
            </th>
            <th scope="col" className="w-[120px] text-left text-xs font-bold text-slate-700">
              Quy Cách &amp; Kệ
            </th>
            <th scope="col" className="w-[80px] text-right text-xs font-bold text-slate-700">
              Lượt Dập
            </th>
            <th scope="col" className="w-[124px] text-center text-xs font-bold text-slate-700">
              Trạng Thái
            </th>
            <th
              scope="col"
              className="w-[322px] text-right pr-2.5 text-xs font-bold text-slate-700 md:sticky md:right-0 z-30 bg-slate-50 !bg-slate-50 [isolation:isolate] border-l border-slate-200 md:shadow-[-2px_0_4px_-1px_rgba(0,0,0,0.06)]"
            >
              Thao Tác
            </th>
          </tr>
          {showFilter && (
            <InlineFilterRow
              columns={MOLD_PLANNING_FILTER_COLUMNS}
              filterFields={MOLD_PLANNING_FILTER_FIELDS}
              filterValues={filterValues}
              onFilterChange={onFilterChange}
            />
          )}
        </thead>
        <tbody>
          {isLoading ? (
            <tr>
              <td colSpan={8} className="text-center py-12 text-slate-500">
                <div className="flex flex-col items-center justify-center gap-2">
                  <Loader2 className="h-6 w-6 animate-spin text-indigo-600" />
                  <p className="text-xs">Đang tải kế hoạch khuôn...</p>
                </div>
              </td>
            </tr>
          ) : items.length === 0 ? (
            <tr>
              <td colSpan={8} className="text-center py-12 text-slate-500">
                <div className="flex flex-col items-center justify-center gap-2">
                  <Layers className="h-8 w-8 text-slate-300" />
                  <p className="text-sm font-medium">
                    Không tìm thấy yêu cầu khuôn nào phù hợp bộ lọc
                  </p>
                  <p className="text-xs text-slate-400">
                    Thử điều chỉnh từ khóa tìm kiếm, kỳ thời gian hoặc chọn thẻ phân loại khuôn khác
                  </p>
                </div>
              </td>
            </tr>
          ) : (
            items.map((tool: any) => {
              const canonicalType = normalizeMoldTypeCode(tool.toolingType);
              const visualMeta =
                taxonomyByCode.get(canonicalType) ||
                getMoldTypeVisualMeta(tool.toolingType);
              const isReady = tool.readinessStatus === "READY";
              const isOutsourced = Boolean(tool.outsourcePoId);
              const firstImage =
                Array.isArray(tool.dieCut?.images) && tool.dieCut.images.length > 0
                  ? tool.dieCut.images[0]
                  : null;
              const customerDisplayName =
                tool.productionOrder?.customer?.fullName ||
                tool.productionOrder?.customer?.name ||
                "Khách lẻ";
              const specText =
                tool.dimensions || tool.dieCut?.size || tool.specs || "";

              return (
                <DataTableRow
                  key={tool.id}
                  id={tool.id}
                  rowSelection={rowSelection}
                  onClick={() => openRow(tool)}
                  className="hover:bg-slate-50/70 transition-colors border-b border-slate-100 h-10 cursor-pointer"
                >
                  {/* Ô chọn dòng */}
                  <td
                    className="w-9 text-center text-xs md:sticky md:left-0 z-20 bg-white !bg-white group-hover:bg-slate-50 [isolation:isolate] border-r border-slate-100"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <DataTableRowCheckbox id={tool.id} rowSelection={rowSelection} asChild />
                  </td>

                  {/* Production Order Info */}
                  <td className="w-[190px] md:sticky md:left-9 z-20 bg-white !bg-white group-hover:bg-slate-50 [isolation:isolate] border-r border-slate-200 md:shadow-[2px_0_4px_-1px_rgba(0,0,0,0.06)]">
                    <div className="flex items-start gap-1.5 min-w-0">
                    <ProductionThumbnailCell
                      imageUrl={tool.productionOrder?.product?.image}
                      layoutUrl={tool.productionOrder?.printLayouts?.[0]?.fileUrl}
                      title={tool.productionOrder?.productName || undefined}
                      subtitle={tool.productionOrder?.orderId}
                      type="product"
                      size={32}
                      className="mt-0.5"
                    />
                    <div className="min-w-0">
                      <div className="font-mono font-bold text-xs text-indigo-700 truncate">
                        {tool.productionOrder?.orderId || "LSX-N/A"}
                      </div>
                      <div
                        className="text-xs font-semibold text-slate-800 truncate mt-0.5"
                        title={tool.productionOrder?.productName || undefined}
                      >
                        {tool.productionOrder?.productName || "Sản phẩm"}
                      </div>
                      <div
                        className="text-[11px] text-slate-500 truncate"
                        title={customerDisplayName}
                      >
                        {customerDisplayName}
                      </div>
                      {tool.productionOrder?.deadline && (
                        <div className="text-[10px] text-slate-400 flex items-center gap-1 mt-0.5 truncate">
                          <Clock className="h-3 w-3 text-slate-400 shrink-0" />
                          <span>
                            Giao: {format(new Date(tool.productionOrder.deadline), "dd/MM/yyyy")}
                          </span>
                        </div>
                      )}
                    </div>
                    </div>
                  </td>

                  {/* Photo & Unique Mold Code */}
                  <td className="apv-col-code w-[154px]">
                    <div className="flex items-center gap-2 min-w-0">
                      {firstImage ? (
                        <span onClick={(e) => e.stopPropagation()} className="shrink-0">
                          <ImageLightbox
                            src={getFullUrl(firstImage)}
                            alt={tool.moldCode || "Khuôn"}
                          >
                            <img
                              src={getFullUrl(firstImage)}
                              alt={tool.moldCode || "Khuôn"}
                              className="h-8 w-8 rounded border border-slate-200 object-cover shrink-0 cursor-pointer hover:opacity-80"
                            />
                          </ImageLightbox>
                        </span>
                      ) : (
                        <div className="h-8 w-8 rounded bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-400 shrink-0">
                          <ImageIcon className="h-3.5 w-3.5" />
                        </div>
                      )}
                      <div className="min-w-0">
                        {tool.dieCut?.id ? (
                          <div className="font-mono font-bold text-xs text-indigo-700 truncate">
                            {tool.moldCode || tool.dieCut.dieCutId || tool.dieCut.id}
                          </div>
                        ) : (
                          <div className="font-mono font-bold text-xs text-slate-900 truncate">
                            {tool.moldCode || "Chưa có mã"}
                          </div>
                        )}
                        <div className="text-[11px] text-slate-500 truncate">
                          {tool.dieCut?.name || tool.productionOrder?.productName || "Khuôn tiêu chuẩn"}
                        </div>
                        {tool.status === "NEW" ? (
                          <Badge
                            variant="outline"
                            className="text-[10px] px-1 py-0 border-amber-300 bg-amber-50 text-amber-700"
                          >
                            Cần làm mới
                          </Badge>
                        ) : (
                          <Badge
                            variant="outline"
                            className="text-[10px] px-1 py-0 border-slate-200 bg-slate-50 text-slate-600"
                          >
                            Dùng lại từ kho
                          </Badge>
                        )}
                      </div>
                    </div>
                  </td>

                  {/* Type & Process */}
                  <td>
                    <div className="min-w-0">
                      <span
                        className={cn(
                          "inline-flex items-center text-[11px] font-bold px-1.5 py-0.5 rounded border whitespace-nowrap",
                          visualMeta.badgeClass,
                        )}
                        title={visualMeta.fullLabel}
                      >
                        [{visualMeta.prefix}] {visualMeta.shortLabel}
                      </span>
                      <div className="text-xs text-slate-600 font-medium mt-1 truncate">
                        {tool.processName || "Công đoạn gia công"}
                      </div>
                      {isOutsourced && (
                        <div className="text-[10px] text-purple-600 font-medium flex items-center gap-1 mt-0.5 truncate">
                          <Hammer className="h-3 w-3 shrink-0" />
                          <span className="truncate">Gia công ngoài ({tool.outsourcePoId})</span>
                        </div>
                      )}
                    </div>
                  </td>

                  {/* Specs & Location */}
                  <td>
                    <div className="text-xs text-slate-700 min-w-0">
                      {specText ? (
                        <div className="font-mono text-[11px] truncate">{specText}</div>
                      ) : (
                        <div className="text-slate-400 italic text-[11px] truncate">Chưa nhập KT dao</div>
                      )}
                      <div className="flex items-center gap-1 text-[11px] text-slate-600 mt-1 min-w-0">
                        <MapPin className="h-3 w-3 text-slate-400 shrink-0" />
                        <span className="font-mono font-bold text-slate-900 truncate">
                          {formatLocation(tool.location || tool.dieCut?.location) || "Chưa xếp kệ"}
                        </span>
                      </div>
                    </div>
                  </td>

                  {/* Imprint Count */}
                  <td className="text-right">
                    {tool.dieCut ? (
                      <MoldWearBar
                        usageCount={tool.dieCut.usageCount ?? 0}
                        maxImpressions={tool.dieCut.maxImpressions}
                        maintenanceInterval={tool.dieCut.maintenanceInterval}
                        lastMaintenanceImpressions={tool.dieCut.lastMaintenanceImpressions}
                        compact={true}
                      />
                    ) : (
                      <div>
                        <div className="font-mono font-bold text-xs text-slate-400">—</div>
                        <div className="text-[10px] text-slate-400">Chưa gắn khuôn</div>
                      </div>
                    )}
                  </td>

                  {/* Readiness Status */}
                  <td className="text-center whitespace-nowrap">
                    {isReady ? (
                      <div className="inline-flex items-center gap-1 text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded-full text-[10.5px] font-bold">
                        <CheckCircle2 className="h-3 w-3 text-emerald-600 shrink-0" />
                        <span>SẴN SÀNG</span>
                      </div>
                    ) : isOutsourced || tool.readinessStatus === "IN_PRODUCTION" ? (
                      <div className="inline-flex items-center gap-1 text-purple-700 bg-purple-50 border border-purple-200 px-1.5 py-0.5 rounded-full text-[10.5px] font-bold">
                        <Hammer className="h-3 w-3 text-purple-600 shrink-0" />
                        <span>ĐANG LÀM</span>
                      </div>
                    ) : (
                      <div className="inline-flex items-center gap-1 text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded-full text-[10.5px] font-bold">
                        <AlertCircle className="h-3 w-3 text-amber-600 shrink-0" />
                        <span>CHỜ CHUẨN BỊ</span>
                      </div>
                    )}
                  </td>

                  {/* Actions */}
                  <td
                    className="w-[322px] text-right pr-2 md:sticky md:right-0 z-20 bg-white !bg-white group-hover:bg-slate-50 [isolation:isolate] border-l border-slate-200 md:shadow-[-2px_0_4px_-1px_rgba(0,0,0,0.06)] whitespace-nowrap"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="flex items-center justify-end gap-1">
                      {!isReady ? (
                        <>
                          {!tool.outsourcePoId && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 !px-2 text-[11px] border-purple-200 text-purple-700 bg-purple-50 hover:bg-purple-100 font-medium"
                              onClick={() => handleOpenOutsource(tool)}
                              title="Đặt làm khuôn ngoài qua Maker"
                            >
                              <Hammer className="h-3 w-3 mr-0.5 text-purple-600 shrink-0" />
                              Đặt Làm Ngoài
                            </Button>
                          )}
                          <Button
                            size="sm"
                            className="h-7 !px-2 text-[11px] bg-emerald-600 hover:bg-emerald-700 text-white font-medium"
                            onClick={() => handleOpenReceive(tool)}
                          >
                            <PackageCheck className="h-3 w-3 mr-0.5 shrink-0" />
                            Nghiệm Thu
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 !px-2 text-[11px] border-slate-200 text-slate-700 bg-white hover:bg-slate-50 font-medium"
                            onClick={() => {
                              setRelocateItem(
                                tool.dieCut || {
                                  id: tool.dieCutId || tool.id,
                                  dieCutId: tool.moldCode,
                                  name: tool.dieCut?.name || tool.moldCode,
                                  location: tool.location,
                                  storageZone: tool.dieCut?.storageZone,
                                  storageRack: tool.dieCut?.storageRack,
                                  storageShelf: tool.dieCut?.storageShelf,
                                  storageBin: tool.dieCut?.storageBin,
                                },
                              );
                              setIsRelocateOpen(true);
                            }}
                            title="Đổi vị trí kệ kho"
                          >
                            <MapPin className="h-3 w-3 mr-0.5 text-slate-500 shrink-0" />
                            Đổi Kệ
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 px-2 text-[11px] border-indigo-200 text-indigo-700 bg-indigo-50/50 hover:bg-indigo-100 font-medium"
                            onClick={() => {
                              setPrintLabelItem(
                                tool.dieCut || {
                                  dieCutId: tool.moldCode,
                                  name:
                                    tool.dieCut?.name ||
                                    `${tool.processName || "Khuôn"} - ${tool.productionOrder?.productName || ""}`,
                                  productName: tool.productionOrder?.productName,
                                  customerName: customerDisplayName,
                                  location: tool.location,
                                  size: tool.dimensions || tool.dieCut?.size,
                                  ups: tool.dieCut?.ups,
                                  maxImpressions: tool.dieCut?.maxImpressions,
                                  usageCount: tool.dieCut?.usageCount,
                                },
                              );
                              setIsPrintLabelOpen(true);
                            }}
                            title="In tem gáy dán khuôn kèm mã QR tra cứu"
                          >
                            <Tag className="h-3 w-3 mr-0.5 text-indigo-600 shrink-0" />
                            In Tem QR
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 px-2 text-[11px] border-slate-200 text-slate-700 bg-white hover:bg-slate-50 font-medium"
                            onClick={() => {
                              setRelocateItem(
                                tool.dieCut || {
                                  id: tool.dieCutId || tool.id,
                                  dieCutId: tool.moldCode,
                                  name: tool.dieCut?.name || tool.moldCode,
                                  location: tool.location,
                                  storageZone: tool.dieCut?.storageZone,
                                  storageRack: tool.dieCut?.storageRack,
                                  storageShelf: tool.dieCut?.storageShelf,
                                  storageBin: tool.dieCut?.storageBin,
                                },
                              );
                              setIsRelocateOpen(true);
                            }}
                            title="Đổi vị trí kệ kho"
                          >
                            <MapPin className="h-3 w-3 mr-0.5 text-slate-500 shrink-0" />
                            Đổi Kệ
                          </Button>
                        </>
                      )}
                    </div>
                  </td>
                </DataTableRow>
              );
            })
          )}
        </tbody>
      </table>

      <BulkActionBar
        selectedCount={selectedCount}
        onClearSelection={clearSelection}
        actions={bulkActions}
      />

      {/* Modal: Nghiệm thu và nhập kệ kho */}
      <Dialog open={isReceiveOpen} onOpenChange={setIsReceiveOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-slate-900">
              <CheckCircle2 className="h-5 w-5 text-emerald-600" />
              Nghiệm Thu &amp; Gán Vị Trí Kệ Kho Khuôn
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Xác nhận khuôn đã kiểm tra đạt quy cách KCS, nghiệm thu vào kệ kho sẵn sàng cho xưởng chạy máy.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-3">
            <div className="bg-emerald-50/60 p-3 rounded border border-emerald-200 space-y-1">
              <div className="text-xs font-medium text-emerald-800">
                Thông tin khuôn nghiệm thu:
              </div>
              <div className="font-mono font-bold text-sm text-emerald-950">
                {activeItem?.moldCode}
              </div>
              <div className="text-xs text-emerald-700">
                LSX:{" "}
                <span className="font-mono font-bold">
                  {activeItem?.productionOrder?.orderId}
                </span>{" "}
                &bull; {activeItem?.productionOrder?.productName}
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-xs font-bold text-slate-700">
                Tọa Độ Giá Kệ Kho (Zone • Rack • Shelf • Bin)
              </label>
              <div className="grid grid-cols-2 gap-2">
                <Input
                  className="h-8 text-xs font-medium"
                  placeholder="Khu (Zone): KHU_A"
                  value={receiveStorageZone}
                  onChange={(e) => setReceiveStorageZone(e.target.value)}
                />
                <Input
                  className="h-8 text-xs font-medium"
                  placeholder="Kệ (Rack): K01"
                  value={receiveStorageRack}
                  onChange={(e) => setReceiveStorageRack(e.target.value)}
                />
                <Input
                  className="h-8 text-xs font-medium"
                  placeholder="Tầng (Shelf): T1"
                  value={receiveStorageShelf}
                  onChange={(e) => setReceiveStorageShelf(e.target.value)}
                />
                <Input
                  className="h-8 text-xs font-medium"
                  placeholder="Ô (Bin): B02"
                  value={receiveStorageBin}
                  onChange={(e) => setReceiveStorageBin(e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-bold text-slate-700">
                Chuỗi Vị Trí Tổng Hợp *
              </label>
              <Input
                className="h-9 font-mono text-xs"
                value={receiveLocation}
                onChange={(e) => setReceiveLocation(e.target.value)}
                placeholder="Ví dụ: KHU_A • Kệ K01 • Tầng T1 • Ô B02"
              />
              <p className="text-[11px] text-slate-500">
                Ghi nhớ vị trí kệ để thợ máy bế / ép nhũ dễ dàng lấy khuôn ngay khi vào ca sản xuất.
              </p>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-bold text-slate-700">
                Ghi Chú Nghiệm Thu KCS
              </label>
              <Input
                value={receiveNotes}
                onChange={(e) => setReceiveNotes(e.target.value)}
                placeholder="Dao sắc bén, cao 23.8mm, nẹp cao su đầy đủ..."
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsReceiveOpen(false)}>
              Hủy
            </Button>
            <Button
              className="bg-emerald-600 hover:bg-emerald-700 text-white font-medium"
              onClick={handleSubmitReceive}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Đang lưu..." : "Xác Nhận Sẵn Sàng"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: Đặt làm khuôn ngoài (Outsource Tooling - Rule 8) */}
      <OutsourceToolingModal
        open={isOutsourceOpen}
        onOpenChange={setIsOutsourceOpen}
        item={outsourceItem}
        onSuccess={invalidateAllMoldQueries}
      />

      {/* Modal: Đổi vị trí kệ kho (Quick Relocate) */}
      <QuickRelocateModal
        open={isRelocateOpen}
        onOpenChange={setIsRelocateOpen}
        tooling={relocateItem}
        toolType="mold"
        onSuccess={invalidateAllMoldQueries}
      />

      {/* Modal: In tem gáy dán khuôn QR */}
      {isPrintLabelOpen && printLabelItem && (
        <PrintDialog
          open={isPrintLabelOpen}
          onClose={() => setIsPrintLabelOpen(false)}
          type="custom"
          documentTitle={`Tem_Gay_Khuon_${printLabelItem.dieCutId || printLabelItem.moldCode}`}
        >
          <MoldLabelPrintTemplate data={printLabelItem} />
        </PrintDialog>
      )}
    </>
  );
}
